import { useMemo } from 'react';
import { useCurrentAgent, useIsCurrentAgentMissing } from '@/features/chats/stores';
import { DEFAULT_RENDER_STATES } from './useChatBubble.constants';
import type { UseChatBubbleProps } from './useChatBubble.types';

export const useChatBubble = ({ content, type }: UseChatBubbleProps) => {
  const agent = useCurrentAgent();
  // An agent whose card cannot be loaded is done loading: its replies still show, under a stand-in.
  const isAgentMissing = useIsCurrentAgentMissing();

  // Memoize agent message processing
  const agentMessage = useMemo(() => {
    if (type === 'user') return null;

    // Handle different content types
    if (typeof content === 'object') {
      return { content };
    }
    // If it's a string, treat it as plain text
    return { content: { text: content } };
  }, [content, type]);

  // Memoize rendering decisions
  const renderingState = useMemo(() => {
    if (type === 'user') {
      return {
        ...DEFAULT_RENDER_STATES,
        shouldRenderUserBubble: true,
      };
    }

    if (!agent && !isAgentMissing) {
      return {
        ...DEFAULT_RENDER_STATES,
        shouldRenderSkeleton: true,
      };
    }

    if (!agentMessage) {
      return {
        ...DEFAULT_RENDER_STATES,
        shouldRenderAgentResponse: true,
        shouldRenderError: true,
      };
    }

    return {
      ...DEFAULT_RENDER_STATES,
      shouldRenderAgentResponse: true,
    };
  }, [type, agent, isAgentMissing, agentMessage]);

  // Memoize processed text content
  const processedText = useMemo(() => {
    if (!agentMessage) return '';

    return typeof agentMessage.content === 'string'
      ? agentMessage.content
      : agentMessage.content.text;
  }, [agentMessage]);

  return {
    renderingState,
    processedText,
  };
};
